import { useCallback, useEffect, useState } from 'react'

import { getSalesPersona } from '@/api/clarityLens/salesLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { SalesPersonaModel } from '@/types/clarityLens'

const SALES_PERSONA_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Persona',
    accessorKey: 'persona',
    type: 'text',
  },
  {
    header: 'Avg. Transaction Count',
    accessorKey: 'avgTransactionCount',
    type: 'number',
  },
  {
    header: 'Most Used Modifier',
    accessorKey: 'mostUsedModifier',
    type: 'text',
  },
  {
    header: '% of Sales',
    accessorKey: 'salesPercentage',
    type: 'percentage',
  },
  {
    header: 'Avg. Order Value',
    accessorKey: 'avgOrderValue',
    type: 'money',
  },
  {
    header: 'Customer Reach',
    accessorKey: 'customerReach',
    type: 'number',
  },
]

export default function useSalesPersona(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [salesPersonaData, setSalesPersonaData] =
    useState<SalesPersonaModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!itemGroupName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await fetchSalesPersonaData(
        branches,
        startDate,
        endDate,
        itemGroupName
      )

      setSalesPersonaData(data)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, startDate, endDate, itemGroupName])

  const refetch = useCallback(() => {
    console.log('Refetching sales persona data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    salesPersonaData,
    SALES_PERSONA_COLUMNS,
    loading,
    error,
    refetch,
  }
}

async function fetchSalesPersonaData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string
): Promise<SalesPersonaModel[]> {
  try {
    const response = await getSalesPersona(
      branches,
      startDate,
      endDate,
      itemGroupName
    )

    const data = response.map((responseItem) => {
      return {
        persona: responseItem.persona,
        avgTransactionCount: responseItem.avg_transaction_count,
        mostUsedModifier: responseItem.most_used_modifier,
        salesPercentage: responseItem.sales_percentage,
        avgOrderValue: responseItem.avg_order_value,
        customerReach: responseItem.customer_reach,
      }
    })

    return data
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}
